import React, { useState, useEffect } from 'react';
import { MdContentCopy, MdCheckCircle, MdArrowBack, MdKeyboardArrowDown } from 'react-icons/md';
import { useNavigate, useLocation } from 'react-router-dom'; 
import Swal from 'sweetalert2';
import './Deposit.css';
import { db } from "../firebase"; 
import { ref, push, onValue } from "firebase/database";
import { useTranslation } from 'react-i18next'; // i18n Hook imported

const ASSET_LOGOS = {
  USDT: "https://cryptologos.cc/logos/tether-usdt-logo.png",
  TRX: "https://cryptologos.cc/logos/tron-trx-logo.png",
  ETH: "https://cryptologos.cc/logos/ethereum-eth-logo.png",
  BTC: "https://cryptologos.cc/logos/bitcoin-btc-logo.png"
};

const Deposit = () => {
  const navigate = useNavigate();
  const location = useLocation(); 
  const { t } = useTranslation(); // Translation initialization
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('USDT'); // Default to USDT
  const from = location.state?.from || 'home';
  const [paymentSettings, setPaymentSettings] = useState({
    address: "Loading...", 
    qrUrl: "" 
  });

  // Mapping Frontend Selection to Firebase Keys
  const currencyMapping = {
    'USDT': 'USDT_TRC20',
  };

  useEffect(() => {
    const dbKey = currencyMapping[selectedCurrency] || 'USDT_TRC20';
    const adminPayRef = ref(db, `adminSettings/payments/${dbKey}`);
    
    const unsub = onValue(adminPayRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setPaymentSettings({
          address: data.address || 'Not Configured',
          qrUrl: data.qrUrl || ''
        });
      } else {
        setPaymentSettings({
          address: 'Address Not Found',
          qrUrl: ''
        });
      }
    }, (error) => {
      console.error("Firebase Fetch Error:", error);
    });

    return () => unsub();
  }, [selectedCurrency]);

  const handleNextStep = async () => {
    if (!amount || parseFloat(amount) < 10) {
      return Swal.fire({
        icon: 'error',
        title: t('invalid_amount_title'),
        text: t('invalid_amount_text'),
        confirmButtonColor: '#f3ba2f'
      });
    }

    if (paymentSettings.address === "Loading..." || paymentSettings.address === "Address Not Found") {
      return Swal.fire({
        icon: 'warning',
        title: t('gateway_error_title'),
        text: t('gateway_error_text'),
        confirmButtonColor: '#f3ba2f'
      });
    }

    const savedUser = JSON.parse(localStorage.getItem('user'));
    const currentTimestamp = Date.now();

    const depositData = {
      username: savedUser.username,
      amount: parseFloat(amount),
      walletAddress: paymentSettings.address,
      currency: selectedCurrency,
      status: "pending",
      timestamp: currentTimestamp,
    };

    try {
      await push(ref(db, 'depositRequests'), depositData);
      setStep(2);
    } catch (error) {
      Swal.fire({ 
        icon: 'error', 
        title: t('error_title'), 
        text: error.message 
      });
    }
  };

  const handleCopy = () => {
    if (paymentSettings.address) {
      navigator.clipboard.writeText(paymentSettings.address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleFinalSubmit = () => {
    Swal.fire({
      icon: 'success',
      title: t('submitted_title'),
      text: t('submitted_text'),
      confirmButtonColor: '#f3ba2f'
    }).then(() => navigate(from === 'mine' ? '/mine' : '/')); 
  };

  return (
    <div className="deposit-wrapper-premium">
      <div className="deposit-nav">
        <MdArrowBack onClick={() => {
          if (step === 2) {
            setStep(1);
          } else {
            navigate(from === 'mine' ? '/mine' : '/');
          }
        }} />
        <span>{t('deposit_title')} {selectedCurrency}</span>
      </div>

      <div className="deposit-content">
        {step === 1 ? (
          <div className="luxury-card animated-fade">
            <div className="card-header-premium">
              <div className="step-indicator">Step 01</div>
              <h3>{t('select_asset')}</h3>
            </div>

            <div className="premium-dropdown-container">
              <div className="custom-select-wrapper">
                <img src={ASSET_LOGOS[selectedCurrency]} alt="logo" className="select-asset-icon" />
                <select 
                  value={selectedCurrency} 
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  className="premium-select"
                >
                  <option value="USDT">USDT (TRC20)</option>
                </select>
                <MdKeyboardArrowDown className="select-arrow" />
              </div>
            </div>
            
            <div className="premium-input-container">
              <label>{t('amount_label')} ({selectedCurrency})</label>
              <div className="input-with-label">
                <input 
                  type="number" 
                  placeholder="0.00" 
                  value={amount} 
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            </div>

            <div className="quick-grid-luxury">
              {[30, 50, 100, 200, 500, 700, 1000, 1500, 2000, 2500, 3000, 5000].map(val => (
                <button key={val} onClick={() => setAmount(val.toString())} className="luxury-amt-btn">
                  ${val}
                </button>
              ))}
            </div>

            <button className="main-gold-btn" onClick={handleNextStep}>
              {t('proceed')}
            </button>
          </div>
        ) : (
          <div className="luxury-card animated-fade">
            <div className="payment-asset-header">
               <img src={ASSET_LOGOS[selectedCurrency]} alt="asset" className="payment-large-logo" />
               <h3>{t('pay')} ${parseFloat(amount).toFixed(2)}</h3>
            </div>

            <div className="qr-container-premium">
              <div className="qr-frame">
                <img 
                  src={paymentSettings.qrUrl || `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${paymentSettings.address}`} 
                  alt="QR" 
                />
              </div>
            </div>

            <div className="wallet-copy-section">
              <div className="copy-field-premium">
                <div className="address-text">{paymentSettings.address}</div>
                <button onClick={handleCopy} className="copy-action-btn">
                  {copied ? <MdCheckCircle color="#0ecb81" size={18} /> : <MdContentCopy size={18} />}
                </button>
              </div>
            </div>

            <div className="luxury-notices">
              <div className="notice-row"><p>• {t('trc20_notice', { currency: selectedCurrency })}</p></div>
              <div className="notice-row"><p>• {t('arrival_notice')}</p></div>
            </div>

            <button className="main-gold-btn pulse-effect" onClick={handleFinalSubmit}>
              {t('i_have_paid')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Deposit;